/**
 * Feature Flag Context
 *
 * Wraps LaunchDarkly's React SDK to provide a clean, app-specific API.
 * All flag reads go through the hooks exported here so the rest of the
 * codebase never imports from `launchdarkly-react-client-sdk` directly.
 *
 * User identification:
 * Reacts to AuthContext user changes and calls ldClient.identify() so
 * LaunchDarkly evaluates flags against the real user (sub) rather than
 * the anonymous context set at startup. Requires AuthProvider to be an
 * ancestor in the tree.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";
import type { FeatureFlagKey } from "../constants/featureFlags";
import { useAuth } from "./AuthContext";

interface FeatureFlagContextState {
  flags: Record<string, unknown>;
  isReady: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextState | undefined>(
  undefined,
);

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const ldClient = useLDClient();
  const { user, loading: authLoading } = useAuth();
  const [flags, setFlags] = useState<Record<string, unknown>>({});
  const [isReady, setIsReady] = useState(false);

  const syncFlags = useCallback(() => {
    if (!ldClient) return;
    setFlags({ ...ldClient.allFlags() });
  }, [ldClient]);

  // Re-identify with LaunchDarkly whenever the authenticated user changes.
  // Waits for AuthContext to finish loading before identifying so we don't
  // make an unnecessary anonymous identify call that could race with the
  // real user identify when auth resolves.
  useEffect(() => {
    if (!ldClient || authLoading) return;

    const context = user
      ? {
          kind: "user" as const,
          key: user.sub,
          name: user.name,
          email: user.email,
        }
      : { kind: "user" as const, key: "anonymous", anonymous: true };

    ldClient.identify(context).then(() => {
      syncFlags();
      setIsReady(true);
    });
  }, [ldClient, user, authLoading, syncFlags]);

  // Keep flags in sync when LaunchDarkly pushes real-time updates.
  useEffect(() => {
    if (!ldClient) return;
    ldClient.on("change", syncFlags);
    return () => ldClient.off("change", syncFlags);
  }, [ldClient, syncFlags]);

  return (
    <FeatureFlagContext.Provider value={{ flags, isReady }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Read a single feature flag value.
 *
 * @param flagKey - A key from the FeatureFlag registry
 * @param defaultValue - Fallback while the SDK initialises (defaults to `false`)
 *
 * @example
 *   const showNewDashboard = useFeatureFlag(FeatureFlag.NEW_DASHBOARD);
 */
export function useFeatureFlag<T = boolean>(
  flagKey: FeatureFlagKey,
  defaultValue: T = false as T,
): T {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlag must be used within FeatureFlagProvider");
  }

  return (context.flags[flagKey] as T) ?? defaultValue;
}

/**
 * Access all flags and the ready state.
 * Prefer `useFeatureFlag` for individual flags.
 */
export function useFeatureFlags(): FeatureFlagContextState {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagProvider");
  }
  return context;
}
